import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Users from './Users';
import AddDiscount from './AddDiscount';
import ViewDiscounts from './ViewDiscounts';
import OrderStatus from './OrderStatus';
import { useAuth } from '../contexts/AuthContext';
import '../styles/AdminPanel.css';

const AdminPanel: React.FC = () => {
  const { isAuthenticated, isAuthorized, isLoading, error, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine active tab based on current path
  const getActiveTab = React.useCallback(() => {
    const path = location.pathname;
    if (path.includes('/add-discount')) return 1;
    if (path.includes('/view-discounts')) return 2;
    if (path.includes('/orders')) return 3;
    return 0; // Default to Users tab
  }, [location]);
  
  const [activeTab, setActiveTab] = useState(getActiveTab());
  
  // Update active tab when location changes
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [getActiveTab]);
  
  const handleTabChange = (tabIndex: number) => {
    setActiveTab(tabIndex);
    switch (tabIndex) {
      case 0:
        navigate('/users');
        break;
      case 1:
        navigate('/add-discount');
        break;
      case 2:
        navigate('/view-discounts');
        break;
      case 3:
        navigate('/orders');
        break;
    }
  };
  
  if (isLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="loading-spinner"></div>
          <p>Loading admin panel...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="auth-container">
        <div className="auth-card error-card">
          <div className="error-icon">⚠️</div>
          <h2>Authentication Error</h2>
          <p>{error}</p>
          <button className="retry-button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="lock-icon">🔒</div>
          <h2>Authentication Required</h2>
          <p>Please log in to access the admin panel.</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthorized) {
    return (
      <div className="auth-container">
        <div className="auth-card error-card">
          <div className="error-icon">🚫</div>
          <h2>Access Denied</h2>
          <p>
            You do not have permission to access the admin panel.
            Only users with Level 3 access can use this feature.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div className="admin-logo">
          <span className="logo-icon">⚙️</span>
          <h1>Admin Panel</h1>
        </div>
        {currentUser && (
          <div className="admin-user">
            <div className="user-avatar">
              {currentUser.name.charAt(0).toUpperCase()}
            </div>
            <div className="user-info">
              <span className="user-greeting">Welcome, {currentUser.name}</span>
              <span className="user-level">Level {currentUser.level + 1}</span>
            </div>
          </div>
        )}
      </header>
      
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 0 ? 'active' : ''}`}
          onClick={() => handleTabChange(0)}
        >
          <span className="tab-icon">👥</span>
          Users
        </button>
        <button 
          className={`tab-button ${activeTab === 1 ? 'active' : ''}`}
          onClick={() => handleTabChange(1)}
        >
          <span className="tab-icon">➕</span>
          Add discount
        </button>
        <button 
          className={`tab-button ${activeTab === 2 ? 'active' : ''}`}
          onClick={() => handleTabChange(2)}
        >
          <span className="tab-icon">📋</span>
          View discounts
        </button>
        <button 
          className={`tab-button ${activeTab === 3 ? 'active' : ''}`}
          onClick={() => handleTabChange(3)}
        >
          <span className="tab-icon">📦</span>
          Orders
        </button>
      </div>
      
      <div className="tab-content">
        <Routes>
          <Route path="/users" element={<Users />} />
          <Route path="/add-discount" element={<AddDiscount />} />
          <Route path="/view-discounts" element={<ViewDiscounts />} />
          <Route path="/orders" element={<OrderStatus />} />
          <Route path="/" element={<Navigate to="/users" replace />} />
          <Route path="*" element={<Navigate to="/users" replace />} />
        </Routes>
      </div>
    </div>
  );
};

export default AdminPanel; 